import { useCallback, useEffect, useState, type FocusEvent, type KeyboardEvent } from 'react';

/** Less than this left to scroll is the end: a rounded `scrollTop` never quite reaches it. */
const END_TOLERANCE_PX = 1;
/** One press of ↑ or ↓, as the browsers scroll a focused box by a line. */
const ARROW_STEP_PX = 40;

interface IBottomScrollState {
  /** The content is taller than the box, so the box scrolls. */
  overflowing: boolean;
  /** Part of the content is still below the box's bottom edge. */
  moreBelow: boolean;
}

/**
 * Whether a capped box that scrolls inside itself has more below its bottom edge (I-1450).
 *
 * A box held to a height (the email banners' 40 dvh, I-0855) clips what does not fit and says
 * nothing about it: at the largest text size the last line and the banner's buttons were 121 px
 * below the edge and nothing on screen told a reader they were there. This answers the two
 * questions the hint needs, measured on the box itself, again on every scroll and whenever the box
 * or anything in it changes size (a text size change, a sentence swapped for "Sent").
 *
 * `ref` is a callback ref, so a box that mounts later (a banner that waits for its answer) is
 * measured when it arrives. `onFocus` scrolls the box to its end when a control in it takes focus:
 * the controls are the box's last row, so a keyboard or VoiceOver user who reaches one sees all of
 * them, with no fade left over them. `onKeyDown` scrolls the focused box by a line on ↑ and ↓ and
 * marks the key answered: a list's keyboard navigation on the page (`useListNavigation`) listens on
 * the document and would otherwise take the arrows and move focus out of the box to a row.
 */
export function useBottomScrollFade<T extends HTMLElement>() {
  const [node, setNode] = useState<T | null>(null);
  const [state, setState] = useState<IBottomScrollState>({ overflowing: false, moreBelow: false });

  useEffect(() => {
    if (!node) return;
    const measure = () => {
      const hidden = node.scrollHeight - node.clientHeight;
      const overflowing = hidden > END_TOLERANCE_PX;
      const moreBelow = overflowing && hidden - node.scrollTop > END_TOLERANCE_PX;
      setState((last) =>
        last.overflowing === overflowing && last.moreBelow === moreBelow
          ? last
          : { overflowing, moreBelow },
      );
    };
    measure();
    node.addEventListener('scroll', measure, { passive: true });
    const resized = new ResizeObserver(measure);
    resized.observe(node);
    for (const child of Array.from(node.children)) resized.observe(child);
    return () => {
      node.removeEventListener('scroll', measure);
      resized.disconnect();
    };
  }, [node]);

  const onFocus = useCallback(
    (event: FocusEvent<T>) => {
      if (!node || event.target === node || !state.overflowing) return;
      node.scrollTop = node.scrollHeight;
    },
    [node, state.overflowing],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<T>) => {
      if (!node || event.target !== node || !state.overflowing) return;
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      event.preventDefault();
      node.scrollBy({ top: event.key === 'ArrowDown' ? ARROW_STEP_PX : -ARROW_STEP_PX });
    },
    [node, state.overflowing],
  );

  return { ref: setNode, onFocus, onKeyDown, ...state };
}

interface IBottomScrollFadeProps {
  /** From `useBottomScrollFade`: drawn only for a box that scrolls. */
  overflowing: boolean;
  moreBelow: boolean;
  /** The gradient's opaque end: the box's own background, so the words fade into it. */
  fromClassName: string;
  /**
   * The box's own side and bottom padding, which the fade covers too, as negative insets: a sticky
   * child stops at the box's content edge, and the fade belongs on its visible edge. By default a
   * top bar's `px-4 py-2.5`, which the build writes in px, so the insets are px too.
   */
  insetClassName?: string;
  /**
   * Its place in the box's own layout: by default a line of its own in a box that wraps its
   * children. A column (the sidebar's, I-1308) takes no line's basis, which there would be a height.
   */
  placementClassName?: string;
  /**
   * `surface` (default): the box's own background over its last 40 px, so words dissolve into it.
   * `shadow` (F-0519, IP-46, I-1659): a neutral 12 px shade at the edge, painted over nothing the box
   * says. A list of rows that fades its last row into the background leaves a row that reads as a
   * ghost (a header seen at 1024 × 768 as a stray chevron); a shade leaves every row whole and says
   * the same: more below.
   */
  tone?: 'surface' | 'shadow';
}

/**
 * The fade over a capped box's bottom edge while more of it is below (I-1450): the last visible
 * line dissolves into the box's own background, the way a list that goes on reads on iOS and
 * macOS. It goes, on a short transition, once the box is scrolled to its end, so it never lies over
 * the box's last row.
 *
 * Drawn as the box's last child, `sticky` at its bottom and no height of its own, so it adds
 * nothing to the box's layout and stays at the visible edge as the content scrolls under it. It is
 * decoration only: hidden from assistive technology, and it takes no press.
 */
export function BottomScrollFade({
  overflowing,
  moreBelow,
  fromClassName,
  insetClassName = '-inset-x-[16px] -bottom-[10px]',
  placementClassName = 'basis-full self-stretch',
  tone = 'surface',
}: IBottomScrollFadeProps) {
  if (!overflowing) return null;
  return (
    <span
      aria-hidden="true"
      data-scroll-fade={moreBelow ? 'shown' : 'hidden'}
      className={`pointer-events-none sticky bottom-0 block h-0 ${placementClassName} transition-opacity duration-150 motion-reduce:transition-none ${
        moreBelow ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <span
        data-scroll-fade-tone={tone}
        className={`absolute bg-gradient-to-t to-transparent ${insetClassName} ${
          tone === 'shadow' ? 'h-3 from-black/10 dark:from-black/40' : `h-10 ${fromClassName}`
        }`}
      />
    </span>
  );
}
